import { Model, type IGlobalAttributes } from "flexlayout-react";
import objectHash from "object-hash";
import { deserialize, serialize, type Tree } from "./layout-utils";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Config } from "./layout-active-context";

const global: IGlobalAttributes = {
  tabEnableRename: false,
  splitterSize: 6,
  rootOrientationVertical: true,
};

type UseModelProps = {
  id: string;
  tree: Tree;
  config: Config;
  updateLayoutTree: (id: string, tree: Tree) => void;
};

// Rendering is very fragile due to the way FlexLayout re-renders: https://github.com/caplin/FlexLayout/issues/456
// Logic/hack to prevent re-rendering of all panels on model change.
export const useModel = ({
  id,
  tree,
  config,
  updateLayoutTree,
}: UseModelProps) => {
  const [model, setModel] = useState<Model>(() => {
    return Model.fromJson({
      global,
      layout: serialize(tree, config),
    });
  });

  // It may be that the model is already updated with the latest tree (e.g. through the onModelChange callback)
  // If that is the case, we do not want to reset the model as this causes all panels to be re-rendered.
  const hashRef = useRef(objectHash(tree));
  useEffect(() => {
    const newHash = objectHash(tree);
    if (hashRef.current === newHash) {
      return;
    }

    hashRef.current = newHash;

    const newLayout = serialize(tree, config);
    const newModel = Model.fromJson({
      global,
      layout: newLayout,
    });
    setModel(newModel);
  }, [tree, config]);

  const onModelChange = useCallback(
    (changedModel: Model) => {
      const newTree = deserialize(changedModel.toJson().layout);

      // toJson will always return a new object.
      // But sometimes the state won't have actually changed despite onModelChange being called.
      // So compare before we update.
      const newHash = objectHash(newTree);
      if (hashRef.current === newHash) {
        return;
      }

      // After updating the tree upstream, this will try to re-render.
      // By already having an update to date ref, it will prevent re-render of all panels.
      hashRef.current = newHash;

      updateLayoutTree(id, newTree);
    },
    [id, updateLayoutTree]
  );

  return useMemo(() => {
    return {
      model,
      onModelChange,
    };
  }, [model, onModelChange]);
};
